import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";

import type { Membership, Profile } from "@tilt/pos-sdk/types";

import { getPosClient } from "../lib/api";
import { writeActiveMembershipId } from "../lib/auth";
import { cn } from "../lib/utils";

/**
 * Scope picker - a compact dropdown that lists every membership the
 * user holds and lets them switch the active one. The pick is sent
 * to POST /pos/v1/profile/active-membership; the server validates the
 * membership belongs to the caller and returns the freshly resolved
 * permission set. We then persist the choice in sessionStorage so
 * subsequent requests carry ``X-Active-Membership-Id``.
 *
 * The dropdown collapses on a single-membership user - there's
 * nothing to choose, so showing a chooser would just be visual noise.
 */

export function ScopeSwitcher({ profile }: { profile: Profile }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const setActive = useMutation({
    mutationFn: (membershipId: string) => getPosClient().setActiveMembership(membershipId),
    onSuccess: async (resp) => {
      writeActiveMembershipId(resp.active_membership_id);
      // Refetch profile FIRST so components re-render with the new
      // active_membership.location.id before any location-keyed queries
      // (departments, payment-rails) fire. Without this, invalidateQueries
      // immediately retriggers those queries with the OLD locationId in
      // their key but the NEW membership ID in the header, which the server
      // rejects with 403.
      await queryClient.refetchQueries({ queryKey: ["profile"] });
      // Now that profile has the new location context, invalidate the rest.
      void queryClient.invalidateQueries();
      setOpen(false);
    },
  });

  if (profile.memberships.length <= 1) {
    return <ActiveLabel membership={profile.active_membership} />;
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-foreground hover:border-line-strong focus:outline-none focus:ring-2 focus:ring-brand-primary"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <ActiveLabel membership={profile.active_membership} />
        <ChevronDown className="h-4 w-4 text-faint" />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-30 mt-2 max-h-80 w-72 overflow-y-auto rounded-md border border-line bg-surface py-1 shadow-lg"
        >
          {profile.memberships.map((m) => {
            const isActive = profile.active_membership?.membership_id === m.membership_id;
            return (
              <li key={m.membership_id}>
                <button
                  type="button"
                  disabled={setActive.isPending}
                  onClick={() => setActive.mutate(m.membership_id)}
                  className={cn(
                    "flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-muted",
                    isActive && "bg-surface-muted",
                  )}
                >
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      isActive ? "text-brand-primary" : "text-transparent",
                    )}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{labelForMembership(m)}</div>
                    <div className="text-xs text-muted">{m.role_slug.replace(/_/g, " ")}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ActiveLabel({ membership }: { membership: Membership | null }) {
  if (!membership) return <span className="text-muted">No active scope</span>;
  return <span>{labelForMembership(membership)}</span>;
}

function labelForMembership(m: Membership): string {
  if (m.location?.name) return m.location.name;
  if (m.organization?.name) return m.organization.name;
  if (m.partner?.name) return m.partner.name;
  return m.membership_id.slice(0, 8);
}
